import React, { useState } from 'react'
import { XMarkIcon, PlusIcon } from '@heroicons/react/24/outline'
import { useMutation, useQuery } from '@tanstack/react-query'
import api from '@/services/api'
import toast from 'react-hot-toast'

interface AddEmployeeModalProps {
  onClose: () => void
  onSuccess: () => void
}

interface EmployeeFormData {
  firstName: string
  lastName: string
  email: string
  phone: string
  department: string
  position: string
  employmentType: 'full_time' | 'part_time' | 'contract'
  baseSalary: string
  salaryCurrency: 'USD' | 'CDF'
  hireDate: string
  managerId?: number
  bankName: string
  bankAccount: string
  transportAllowance: string
  telephoneAllowance: string
  dataAllowance: string
}

interface Manager {
  id: number
  firstName: string
  lastName: string
  employeeNumber: string
}

const AddEmployeeModal: React.FC<AddEmployeeModalProps> = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState<EmployeeFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    department: '',
    position: '',
    employmentType: 'full_time',
    baseSalary: '',
    salaryCurrency: 'USD',
    hireDate: new Date().toISOString().split('T')[0],
    bankName: '',
    bankAccount: '',
    transportAllowance: '0',
    telephoneAllowance: '0',
    dataAllowance: '0',
  })

  // Fetch potential managers
  const { data: managers } = useQuery<{ employees: Manager[] }>({
    queryKey: ['potential-managers'],
    queryFn: async () => {
      const response = await api.get('/employees?limit=100')
      return response.data.data
    }
  })

  const addEmployeeMutation = useMutation({
    mutationFn: async (data: EmployeeFormData) => {
      const response = await api.post('/employees', data)
      return response.data
    },
    onSuccess: () => {
      toast.success('Employee added successfully')
      onSuccess()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to add employee')
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    addEmployeeMutation.mutate(formData)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: name === 'managerId' ? (value ? parseInt(value) : undefined) : value
    }))
  }

  const departments = [
    'Human Resources',
    'Information Technology',
    'Finance',
    'Sales',
    'Marketing',
    'Operations',
    'Legal',
    'Administration'
  ]

  const getCurrencySymbol = () => {
    return formData.salaryCurrency === 'USD' ? '$' : 'FC'
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm transition-opacity" 
          onClick={(e) => e.stopPropagation()}
        ></div>

        {/* Modal panel */}
        <div className="inline-block w-full max-w-2xl my-8 text-left align-middle bg-white dark:bg-gray-800 rounded-2xl shadow-2xl transform transition-all overflow-hidden">
          
          {/* Header */}
          <div className="relative bg-gradient-to-r from-[#3b82f6] to-[#6366f1] px-6 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#10b981]/20 rounded-lg">
                  <PlusIcon className="h-6 w-6 text-[#10b981]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">
                    Add New Employee
                  </h3>
                  <p className="text-[#10b981] text-sm mt-1">
                    Create a new employee record in the system
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white transition-colors p-2 rounded-lg hover:bg-white/10"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Form Content */}
          <div className="bg-white dark:bg-gray-800 px-6 py-6 max-h-[75vh] overflow-y-auto">
            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Personal Information Section */}
              <div className="bg-gray-50/50 dark:bg-gray-700/50 rounded-xl p-6 border border-gray-200 dark:border-gray-600">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <div className="w-1 h-6 bg-[#10b981] rounded-full mr-3"></div>
                  Personal Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      id="firstName"
                      required
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-[#10b981]/50 focus:border-[#10b981] transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      placeholder="Enter first name"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      id="lastName"
                      required
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-[#10b981]/50 focus:border-[#10b981] transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      placeholder="Enter last name"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-[#10b981]/50 focus:border-[#10b981] transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      placeholder="employee@company.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      id="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+243 81 234 5678"
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-[#10b981]/50 focus:border-[#10b981] transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    />
                  </div>
                </div>
              </div>

              {/* Job Information Section */}
              <div className="bg-gray-50/50 dark:bg-gray-700/50 rounded-xl p-6 border border-gray-200 dark:border-gray-600">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <div className="w-1 h-6 bg-[#10b981] rounded-full mr-3"></div>
                  Job Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="department" className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                      Department *
                    </label>
                    <select
                      name="department"
                      id="department"
                      required
                      value={formData.department}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-[#10b981]/50 focus:border-[#10b981] transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">Select Department</option>
                      {departments.map(dept => (
                        <option key={dept} value={dept}>{dept}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="position" className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                      Position *
                    </label>
                    <input
                      type="text"
                      name="position"
                      id="position"
                      required
                      value={formData.position}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-[#10b981]/50 focus:border-[#10b981] transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      placeholder="e.g., Software Engineer"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label htmlFor="employmentType" className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                      Employment Type *
                    </label>
                    <select
                      name="employmentType"
                      id="employmentType"
                      required
                      value={formData.employmentType}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-[#10b981]/50 focus:border-[#10b981] transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="full_time">Full Time</option>
                      <option value="part_time">Part Time</option>
                      <option value="contract">Contract</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="hireDate" className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                      Hire Date *
                    </label>
                    <input
                      type="date"
                      name="hireDate"
                      id="hireDate"
                      required
                      value={formData.hireDate}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-[#10b981]/50 focus:border-[#10b981] transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label htmlFor="managerId" className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                    Reports To (Manager)
                  </label>
                  <select
                    name="managerId"
                    id="managerId"
                    value={formData.managerId || ''}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-[#10b981]/50 focus:border-[#10b981] transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">No Manager (Independent)</option>
                    {managers?.employees?.map(manager => (
                      <option key={manager.id} value={manager.id}>
                        {manager.firstName} {manager.lastName} (#{manager.employeeNumber})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Compensation Section */}
              <div className="bg-gray-50/50 dark:bg-gray-700/50 rounded-xl p-6 border border-gray-200 dark:border-gray-600">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <div className="w-1 h-6 bg-[#10b981] rounded-full mr-3"></div>
                  Compensation & Benefits
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label htmlFor="baseSalary" className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                      Base Salary *
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 font-medium">
                        {getCurrencySymbol()}
                      </span>
                      <input
                        type="number"
                        name="baseSalary"
                        id="baseSalary"
                        required
                        min="0"
                        step="0.01"
                        value={formData.baseSalary}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-[#10b981]/50 focus:border-[#10b981] transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="salaryCurrency" className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                      Currency *
                    </label>
                    <select
                      name="salaryCurrency"
                      id="salaryCurrency"
                      required
                      value={formData.salaryCurrency}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-[#10b981]/50 focus:border-[#10b981] transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="USD">USD ($)</option>
                      <option value="CDF">CDF (FC)</option>
                    </select>
                  </div>
                </div>

                {/* Allowances */}
                <div className="mt-4">
                  <h5 className="text-md font-semibold text-gray-900 dark:text-white mb-3">Monthly Allowances</h5>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label htmlFor="transportAllowance" className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                        Transport
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 text-sm">
                          {getCurrencySymbol()}
                        </span>
                        <input
                          type="number"
                          name="transportAllowance"
                          id="transportAllowance"
                          min="0"
                          step="0.01"
                          value={formData.transportAllowance}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-[#10b981]/50 focus:border-[#10b981] transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="telephoneAllowance" className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                        Telephone
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 text-sm">
                          {getCurrencySymbol()}
                        </span>
                        <input
                          type="number"
                          name="telephoneAllowance"
                          id="telephoneAllowance"
                          min="0"
                          step="0.01"
                          value={formData.telephoneAllowance}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-[#10b981]/50 focus:border-[#10b981] transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="dataAllowance" className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                        Data
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 text-sm">
                          {getCurrencySymbol()}
                        </span>
                        <input
                          type="number"
                          name="dataAllowance"
                          id="dataAllowance"
                          min="0"
                          step="0.01"
                          value={formData.dataAllowance}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-[#10b981]/50 focus:border-[#10b981] transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                    Allowances are added to the base salary and are taxable
                  </p>
                </div>
              </div>

              {/* Bank Details Section */}
              <div className="bg-gray-50/50 dark:bg-gray-700/50 rounded-xl p-6 border border-gray-200 dark:border-gray-600">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <div className="w-1 h-6 bg-[#10b981] rounded-full mr-3"></div>
                  Bank Details
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="bankName" className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                      Bank Name
                    </label>
                    <input
                      type="text"
                      name="bankName"
                      id="bankName"
                      value={formData.bankName}
                      onChange={handleInputChange}
                      placeholder="e.g., Equity BCDC"
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-[#10b981]/50 focus:border-[#10b981] transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    />
                  </div>
                  <div>
                    <label htmlFor="bankAccount" className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                      Account Number
                    </label>
                    <input
                      type="text"
                      name="bankAccount"
                      id="bankAccount"
                      value={formData.bankAccount}
                      onChange={handleInputChange}
                      placeholder="e.g., 1234567890"
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-[#10b981]/50 focus:border-[#10b981] transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    />
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-600">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors shadow-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addEmployeeMutation.isPending}
                  className="px-6 py-3 bg-[#10b981] text-white rounded-xl text-sm font-medium hover:bg-[#0da271] transition-colors shadow-lg shadow-[#10b981]/25 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {addEmployeeMutation.isPending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Adding Employee...
                    </>
                  ) : (
                    <>
                      <PlusIcon className="h-4 w-4 mr-2" />
                      Add Employee
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AddEmployeeModal